import { ValidationFormField } from '@eventbrite/eds-validation-form-field';
import { GenericLazyString } from '@eventbrite/i18n';
import {
    getFormValidationAdornment,
    TextField,
    TextFieldProps,
    VFFTextFieldAdapter,
} from '@eventbrite/marmalade';
import { shouldDisplayError } from '@eventbrite/redux-form-tools';
import classNames from 'classnames';
import React from 'react';
import RebrandingCheck from '../../resources/rebranding-check';
import RebrandingDangerIcon from '../../resources/rebranding-danger-icon';
import { PROMO_CODE_FIELD_NAME } from './constants';
import rebrandStyles from './PromoCodeInputField.rebranding.module.scss';

const DEBOUNCE_TIME = 500;

export interface PromoCodeInputFieldProps {
    cta: React.ReactNode;
    handleSubmit: () => void;
    helperText?: string;
    inputLabel: string | GenericLazyString;
    inputPlaceholder: string | GenericLazyString;
    isAppRebrandActive: boolean;
    isCodeSuccessful: boolean;
    onChange: () => void;
    submitFailed: boolean;
}

export const PromoCodeInputField: React.FunctionComponent<
    PromoCodeInputFieldProps
> = ({
    cta,
    handleSubmit,
    helperText,
    inputLabel,
    inputPlaceholder,
    isAppRebrandActive,
    isCodeSuccessful,
    onChange,
    submitFailed,
}) => {
    const styles = isAppRebrandActive ? rebrandStyles : {};

    const formClassName = classNames('responsive-form', 'eds-g-group');

    const errorIcon = isAppRebrandActive ? <RebrandingDangerIcon /> : undefined;
    const validIcon = isAppRebrandActive ? (
        <RebrandingCheck width="12" height="11" />
    ) : undefined;

    const adornment = getFormValidationAdornment({
        hasError: submitFailed,
        isValid: isCodeSuccessful,
        errorIcon,
        validIcon,
    });

    let timer: NodeJS.Timeout | null = null;
    const debouncedSubmit = (e: any) => {
        e.preventDefault();
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        timer = setTimeout(handleSubmit, DEBOUNCE_TIME);
    };

    const labelClassnames = classNames(styles?.label, styles?.default, {
        [styles?.labelWithError]: submitFailed,
    });

    const wrapperClassnames = classNames(styles?.wrapper, styles?.default, {
        [styles?.wrapperWithError]: submitFailed,
    });

    const validationProps = {
        asideCustomClassName: classNames(styles?.aside, styles?.default, {
            [styles?.asideWithError]: submitFailed,
        }),
        // TODO: there is a small bug on how the type for this prop is defined on Marmalade,
        // so this typing is a workaround in the meantime
        validationAdornmentPlacement: isAppRebrandActive ? 'helper' : 'input',
    } as unknown as TextFieldProps;

    return (
        <form
            className={formClassName}
            onSubmit={debouncedSubmit}
            data-automation="promo-code-form"
            data-testid="promo-code-form"
        >
            <div className="eds-g-cell eds-g-cell-12-12">
                <ValidationFormField
                    name={PROMO_CODE_FIELD_NAME}
                    shouldDisplayError={shouldDisplayError}
                    v2={true}
                >
                    <VFFTextFieldAdapter onChange={onChange}>
                        <TextField
                            {...validationProps}
                            data-automation="promo-code-field"
                            data-spec="promo-code-field"
                            disabled={isCodeSuccessful}
                            id="promo-code-field"
                            label={inputLabel}
                            placeholder={inputPlaceholder}
                            suffix={cta}
                            type="text"
                            validationAdornment={adornment}
                            helperText={helperText}
                            labelCustomClassName={labelClassnames}
                            customClassName={wrapperClassnames}
                            inputCustomClassName={styles?.input}
                        />
                    </VFFTextFieldAdapter>
                </ValidationFormField>
            </div>
        </form>
    );
};
